import { useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { LanguageCode } from '../../core/i18n/types';
import { languages, defaultLanguage } from '../../core/i18n/config';


type TFunction = {
    (key: string, options?: object): string;
    <T extends object>(key: string, options?: T): string;
  };

// Create the useLanguage hook
export const useLanguage = () => {
  // Directly use the useTranslation hook without type annotations
  const { i18n, t } = useTranslation();

  // Function to change the language
  const changeLanguage = useCallback(
    async (language: LanguageCode) => {
      if (i18n && typeof i18n.changeLanguage === "function") {
        await i18n.changeLanguage(language);
        localStorage.setItem('lang', language);
      } else {
        console.error("i18n.changeLanguage is not available");
      }
    },
    [i18n]
  );

  // Function to get the current language
  const getCurrentLanguage = useCallback((): LanguageCode => {
    if (i18n && i18n.language) {
      const language = i18n.language.split('-')[0];
      if (Object.keys(languages).includes(language)) {
        return language as LanguageCode;
      }
    }

    // Try from localStorage
    const storedLanguage = localStorage.getItem('lang');
    if (storedLanguage && Object.keys(languages).includes(storedLanguage)) {
      return storedLanguage as LanguageCode;
    }

    return defaultLanguage;
  }, [i18n]);

  // Current language
  const currentLanguage = getCurrentLanguage();

  // Get available languages
  const availableLanguages = languages;

  return {
    t: t as TFunction,
    currentLanguage,
    availableLanguages,
    changeLanguage,
    getCurrentLanguage,
  };
};

export default useLanguage;
